import { useLocalization } from "../../../features/localization/index.js";
import { createResponseStreamParser } from "../helpers/response-stream.js";
import CollapsibleSection_default from "../../../components/layout/CollapsibleSection.vue.js";
import { createBlock, createCommentVNode, createElementBlock, createElementVNode, createTextVNode, createVNode, defineComponent, nextTick, onBeforeUnmount, openBlock, ref, toDisplayString, unref, watch, withCtx } from "vue";
import { ScalarButton } from "@scalar/components/button";
import { getUtf8ByteLength } from "@scalar/helpers/string/get-utf8-byte-length";
import { useClipboard } from "@scalar/use-hooks/useClipboard";
import { ScalarLoading, useLoadingState } from "@scalar/components/loading";
import { formatBytes } from "@scalar/helpers/formatters/format-bytes";
//#region src/v2/blocks/response-block/components/ResponseBodyStreaming.vue?vue&type=script&setup=true&lang.ts
var _hoisted_1 = { class: "flex w-full items-center justify-between" };
var _hoisted_2 = { class: "text-c-2 ml-2 text-xs" };
var _hoisted_3 = {
	key: 0,
	class: "mr-2 flex items-center gap-2"
};
var _hoisted_4 = { class: "text-c-2" };
var _hoisted_5 = {
	key: 0,
	class: "text-red bg-b-danger sticky top-0 border-b p-2"
};
var _hoisted_6 = {
	key: 1,
	class: "p-2"
};
var MAX_DISPLAY_SIZE = 16777216;
var ResponseBodyStreaming_vue_vue_type_script_setup_true_lang_default = /*@__PURE__*/ defineComponent({
	__name: "ResponseBodyStreaming",
	props: {
		reader: {},
		contentType: { default: "text/event-stream" }
	},
	setup(__props) {
		const { translate } = useLocalization();
		const loader = useLoadingState();
		const textContent = ref("");
		const receivedBytes = ref(0);
		const { copyToClipboard } = useClipboard();
		let downloadUrl;
		const clearDownload = () => {
			if (downloadUrl) {
				URL.revokeObjectURL(downloadUrl);
				downloadUrl = void 0;
			}
		};
		/** Export only the bounded displayed transcript, without retaining another raw body. */
		const downloadText = () => {
			clearDownload();
			downloadUrl = URL.createObjectURL(new Blob([textContent.value], { type: "text/plain;charset=utf-8" }));
			const link = document.createElement("a");
			link.href = downloadUrl;
			link.download = "response-stream.txt";
			link.click();
		};
		const errorRef = ref(null);
		const contentContainer = ref(null);
		let currentReader = null;
		/** Bound the text retained by a connection that can stay open indefinitely. */
		watch(textContent, async () => {
			await nextTick();
			if (contentContainer.value) contentContainer.value.scrollTop = contentContainer.value.scrollHeight;
		});
		const cancelReader = (streamReader) => {
			Promise.resolve(streamReader.cancel()).catch(() => {});
		};
		const readStream = async (streamReader) => {
			try {
				let displayedBytes = 0;
				const parser = createResponseStreamParser(__props.contentType, (text) => {
					displayedBytes += getUtf8ByteLength(text);
					if (displayedBytes > MAX_DISPLAY_SIZE) throw new Error("Stream display reached its 16 MiB limit. Cancelled further reading.");
					textContent.value += text;
				});
				while (currentReader === streamReader) {
					const { done, value } = await streamReader.read();
					if (currentReader !== streamReader) return;
					if (done) {
						parser.finish();
						loader.clear();
						return;
					}
					receivedBytes.value += value.byteLength;
					parser.push(value);
				}
			} catch (error) {
				if (currentReader === streamReader) {
					console.error("Error reading stream:", error);
					errorRef.value = error instanceof Error ? error : new Error(String(error));
					cancelReader(streamReader);
					loader.clear();
				}
			} finally {
				streamReader.releaseLock();
			}
		};
		const stopStreaming = () => {
			const previous = currentReader;
			currentReader = null;
			if (previous) cancelReader(previous);
			loader.clear();
		};
		const startStreaming = () => {
			stopStreaming();
			currentReader = __props.reader;
			loader.start();
			clearDownload();
			receivedBytes.value = 0;
			textContent.value = "";
			errorRef.value = null;
			readStream(__props.reader);
		};
		watch(() => __props.reader, startStreaming, { immediate: true });
		onBeforeUnmount(() => {
			stopStreaming();
			clearDownload();
		});
		return (_ctx, _cache) => {
			return openBlock(), createBlock(unref(CollapsibleSection_default), { class: "max-h-content overflow-y-hidden" }, {
				title: withCtx(() => [createElementVNode("div", _hoisted_1, [createElementVNode("div", null, [createTextVNode(toDisplayString(unref(translate)("apiClient.responseBodyStreaming.body")) + " ", 1), createElementVNode("span", _hoisted_2, toDisplayString(unref(formatBytes)(receivedBytes.value)) + " received ", 1)]), unref(loader).isLoading ? (openBlock(), createElementBlock("div", _hoisted_3, [createVNode(unref(ScalarLoading), {
					loadingState: unref(loader),
					size: "xs"
				}, null, 8, ["loadingState"]), createElementVNode("span", _hoisted_4, toDisplayString(unref(translate)("apiClient.responseBodyStreaming.listening")), 1)])) : createCommentVNode("", true)])]),
				actions: withCtx(() => [
					unref(loader).isLoading ? (openBlock(), createBlock(unref(ScalarButton), {
						key: 0,
						size: "sm",
						variant: "ghost",
						onClick: stopStreaming
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.responseBodyStreaming.cancel")), 1)]),
						_: 1
					})) : createCommentVNode("", true),
					textContent.value ? (openBlock(), createBlock(unref(ScalarButton), {
						key: 1,
						size: "sm",
						variant: "ghost",
						onClick: _cache[0] || (_cache[0] = ($event) => unref(copyToClipboard)(textContent.value))
					}, {
						default: withCtx(() => [..._cache[1] || (_cache[1] = [createTextVNode(" Copy text ", -1)])]),
						_: 1
					})) : createCommentVNode("", true),
					textContent.value ? (openBlock(), createBlock(unref(ScalarButton), {
						key: 2,
						size: "sm",
						variant: "ghost",
						onClick: downloadText
					}, {
						default: withCtx(() => [..._cache[2] || (_cache[2] = [createTextVNode(" Download text ", -1)])]),
						_: 1
					})) : createCommentVNode("", true)
				]),
				default: withCtx(() => [createElementVNode("div", {
					ref_key: "contentContainer",
					ref: contentContainer,
					class: "text-xxs font-code h-full overflow-auto leading-6 whitespace-pre-wrap"
				}, [errorRef.value ? (openBlock(), createElementBlock("div", _hoisted_5, toDisplayString(errorRef.value.message), 1)) : createCommentVNode("", true), textContent.value ? (openBlock(), createElementBlock("div", _hoisted_6, toDisplayString(textContent.value), 1)) : createCommentVNode("", true)], 512)]),
				_: 1
			});
		};
	}
});
//#endregion
export { ResponseBodyStreaming_vue_vue_type_script_setup_true_lang_default as default };

//# sourceMappingURL=ResponseBodyStreaming.vue_vue_type_script_setup_true_lang.js.map